import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { IRecruiterDashboardMetrics } from '../../../../hooks/useRecruiterDashboardMetrics';
import { IRecruiterDashboardSettings } from '../../../../hooks/useRecruiterDashboardSettings';
import { IRecruiterFilters } from '../../utils/recruiterFilters';
import {
  ActiveMetricModal,
  IChurnDetailsData,
  IDateBounds,
  IDropoutDetailsData,
  IModalFilterContext,
  IModalLabels,
  IRatioDetailsData,
  IRetentionDetailsData,
  IShowRateDetailsData,
} from '../types';
import { buildChurnModalData } from '../utils/builders/buildChurnModalData';
import { buildDropoutModalData } from '../utils/builders/buildDropoutModalData';
import { buildRatioModalData } from '../utils/builders/buildRatioModalData';
import { buildRetentionModalData } from '../utils/builders/buildRetentionModalData';
import { buildShowRateModalData } from '../utils/builders/buildShowRateModalData';
import { IThresholdStatus } from '../utils/getThresholdStatus';

interface IUseRecruiterMetricModalsInput {
  dashboardMetrics: IRecruiterDashboardMetrics | null;
  settings: IRecruiterDashboardSettings | null;
  dateBounds: IDateBounds;
  quarterlyDateBounds: IDateBounds;
  filters: IRecruiterFilters;
  locationIds?: string[];
  dateRangeLabel: string;
}

export interface IRecruiterMetricModals {
  activeModal: ActiveMetricModal;
  open: (modal: Exclude<ActiveMetricModal, null>) => void;
  close: () => void;
  churnPeriodData: IChurnDetailsData | null;
  churnQuarterlyData: IChurnDetailsData | null;
  retentionDetailsData: IRetentionDetailsData | null;
  dropoutDetailsData: IDropoutDetailsData | null;
  ratioDetailsData: IRatioDetailsData | null;
  showRateDetailsData: IShowRateDetailsData | null;
  modalFilterProps: IModalFilterContext;
  /** Threshold statuses consumed by the dashboard cards (not the modals). */
  periodChurnStatus: IThresholdStatus;
  quarterlyChurnStatus: IThresholdStatus;
  showRateStatus: IThresholdStatus;
}

export function useRecruiterMetricModals({
  dashboardMetrics,
  settings,
  dateBounds,
  quarterlyDateBounds,
  filters,
  locationIds,
  dateRangeLabel,
}: IUseRecruiterMetricModalsInput): IRecruiterMetricModals {
  const { t: tModals } = useTranslation('dashboard.RecruiterDashboard.modals');

  const [activeModal, setActiveModal] = useState<ActiveMetricModal>(null);
  const open = useCallback(
    (modal: Exclude<ActiveMetricModal, null>) => setActiveModal(modal),
    []
  );
  const close = useCallback(() => setActiveModal(null), []);

  const modalLabels = useMemo<IModalLabels>(
    () => ({
      currentPeriod: tModals('currentPeriodLabel', { dateRangeLabel }),
      thisQuarter: tModals('thisQuarterLabel'),
      statusGood: tModals('statusLabels.good'),
      statusOk: tModals('statusLabels.ok'),
      statusPoor: tModals('statusLabels.poor'),
      statusNeedsAttention: tModals('statusLabels.needsAttention'),
      statusExcellent: tModals('statusLabels.excellent'),
    }),
    [tModals, dateRangeLabel]
  );

  const {
    churnPeriodData,
    churnQuarterlyData,
    periodChurnStatus,
    quarterlyChurnStatus,
  } = useMemo(
    () =>
      buildChurnModalData(
        dashboardMetrics,
        settings,
        dateBounds,
        quarterlyDateBounds,
        modalLabels
      ),
    [dashboardMetrics, settings, dateBounds, quarterlyDateBounds, modalLabels]
  );

  const retentionDetailsData = useMemo(
    () => buildRetentionModalData(dashboardMetrics, dateBounds, modalLabels),
    [dashboardMetrics, dateBounds, modalLabels]
  );

  const dropoutDetailsData = useMemo(
    () =>
      buildDropoutModalData(dashboardMetrics, quarterlyDateBounds, modalLabels),
    [dashboardMetrics, quarterlyDateBounds, modalLabels]
  );

  const ratioDetailsData = useMemo(
    () => buildRatioModalData(dashboardMetrics, dateBounds, modalLabels),
    [dashboardMetrics, dateBounds, modalLabels]
  );

  const { showRateDetailsData, showRateStatus } = useMemo(
    () =>
      buildShowRateModalData(
        dashboardMetrics,
        settings,
        dateBounds,
        modalLabels
      ),
    [dashboardMetrics, settings, dateBounds, modalLabels]
  );

  const modalFilterProps = useMemo<IModalFilterContext>(
    () => ({
      dateRange: filters.dateRange,
      customDateRange: filters.customDateRange,
      regionIds: filters.regionIds,
      officeIds: filters.officeIds,
      locationIds,
      activeDonorStatuses: settings?.activeDonorStatuses,
    }),
    [
      filters.dateRange,
      filters.customDateRange,
      filters.regionIds,
      filters.officeIds,
      locationIds,
      settings?.activeDonorStatuses,
    ]
  );

  return {
    activeModal,
    open,
    close,
    churnPeriodData,
    churnQuarterlyData,
    retentionDetailsData,
    dropoutDetailsData,
    ratioDetailsData,
    showRateDetailsData,
    modalFilterProps,
    periodChurnStatus,
    quarterlyChurnStatus,
    showRateStatus,
  };
}
